import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarClock,
  Feather,
  Flame,
  Hash,
  Loader2,
  MapPin,
  MessagesSquare,
  Plus,
  Quote,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsLayout } from '@/components/smchs/Shell';
import { X } from 'lucide-react';
import { TokenManager } from '@/lib/token-manager';
import {
  BookClub,
  BookClubStats,
  ClubMeeting,
  DiscussionThread,
  FeaturedVoice,
  ProposalData,
  bookclubClubsApi,
  bookclubDiscussionsApi,
  bookclubFeaturedVoiceApi,
  bookclubMeetingsApi,
  bookclubMembershipApi,
  bookclubProposalsApi,
  bookclubStatsApi,
} from '@/services/bookclub-api';

/* ===================================================================== *
 *  Book Club: wired to /api/v1/bookclub/ endpoints.
 * ===================================================================== */

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function formatMeetingTime(start: string, end: string): string {
  const fmt = (t: string) => t.slice(0, 5);
  return `${fmt(start)} – ${fmt(end)}`;
}

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseMeetingDate(dateStr: string) {
  const d = new Date(`${dateStr}T00:00:00`);
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: MONTH_ABBR[d.getMonth()],
    weekday: DAY_ABBR[d.getDay()],
  };
}

const BookClubPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<BookClubStats | null>(null);
  const [clubs, setClubs] = useState<BookClub[]>([]);
  const [discussions, setDiscussions] = useState<DiscussionThread[]>([]);
  const [featuredVoice, setFeaturedVoice] = useState<FeaturedVoice | null>(null);
  const [meetings, setMeetings] = useState<ClubMeeting[]>([]);
  const [proposeOpen, setProposeOpen] = useState(false);

  useEffect(() => {
    bookclubStatsApi.getStats().then(setStats).catch(() => null);
    bookclubClubsApi.getClubs({ page_size: 12 }).then((r) => setClubs(r.results)).catch(() => null);
    bookclubDiscussionsApi
      .getDiscussions({ ordering: '-last_reply_at', page_size: 6 })
      .then((r) => setDiscussions(r.results))
      .catch(() => null);
    bookclubFeaturedVoiceApi.getFeaturedVoice().then(setFeaturedVoice).catch(() => null);
    bookclubMeetingsApi.getMeetings({ days: 14, page_size: 8 }).then((r) => setMeetings(r.results)).catch(() => null);
  }, []);

  function requireAuth(then: () => void) {
    if (TokenManager.isTokenValid()) {
      then();
    } else {
      navigate('/signin');
    }
  }

  function handleJoin(club: BookClub) {
    requireAuth(async () => {
      try {
        await bookclubMembershipApi.joinClub(club.slug);
        toast.success(`Joined ${club.name}!`);
        setClubs((prev) =>
          prev.map((c) => (c.slug === club.slug ? { ...c, member_count: c.member_count + 1 } : c))
        );
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          toast.info(`You're already a member of ${club.name}.`);
        } else {
          toast.error('Could not join the club. Please try again.');
        }
      }
    });
  }

  function handleProposeClick() {
    requireAuth(() => setProposeOpen(true));
  }

  return (
    <SmchsLayout>
      <Hero />
      <StatsStrip stats={stats} />
      <ActiveClubs clubs={clubs} onJoin={handleJoin} />
      <LatestDiscussions discussions={discussions} />
      {featuredVoice && <FeaturedVoiceSection voice={featuredVoice} />}
      <ThisWeekCalendar meetings={meetings} />
      <StartAClub onPropose={handleProposeClick} />
      <ProposeClubModal open={proposeOpen} onClose={() => setProposeOpen(false)} />
    </SmchsLayout>
  );
};

/* =============================================================== */
/*  HERO                                                            */
/* =============================================================== */

const Hero: React.FC = () => (
  <section className="relative overflow-hidden border-b border-[var(--border)]">
    <div className="absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(30,45,92,0.12),transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_80%_at_50%_0%,black,transparent_70%)] opacity-40" />
    </div>

    <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
      <Kicker label="The Conversation" />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="col-span-1 lg:col-span-8 font-display font-[900] text-[2rem] sm:text-[3.25rem] md:text-[4rem] lg:text-[5rem] leading-[0.95] tracking-[-0.03em] text-[var(--ink)]"
        >
          Where books become
          <span className="block">
            <span className="font-serif italic font-normal text-[var(--primary)]">conversations</span>.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="col-span-1 lg:col-span-4 text-[var(--ink-soft)] text-[1rem] leading-[1.55]"
        >
          Student-run clubs that meet in the library, read one book at a time
          and talk it through. Find your people, find your next book.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-10 flex flex-wrap items-center gap-3"
      >
        <a
          href="#clubs"
          className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] px-5 py-3 rounded-xl font-medium text-[14px] hover:bg-[var(--primary)] transition-colors"
        >
          Explore the clubs <ArrowRight className="w-4 h-4" />
        </a>
        <a
          href="#start"
          className="inline-flex items-center gap-2 border border-[var(--border)] hover:border-[var(--ink)]/40 text-[var(--ink)] px-5 py-3 rounded-xl font-medium text-[14px] transition-colors bg-[var(--surface)]"
        >
          <Plus className="w-4 h-4" /> Start your own
        </a>
      </motion.div>
    </div>
  </section>
);

/* =============================================================== */
/*  STATS                                                           */
/* =============================================================== */

const StatsStrip: React.FC<{ stats: BookClubStats | null }> = ({ stats }) => {
  const items = [
    { label: 'Active clubs', value: stats?.active_clubs ?? '—' },
    { label: 'Total members', value: stats?.total_members ?? '—' },
    { label: 'Threads this week', value: stats?.threads_this_week ?? '—' },
    { label: 'Books finished this term', value: stats?.books_finished_this_term ?? '—' },
  ];

  return (
    <section className="border-b border-[var(--border)] bg-[var(--surface-2)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
        {items.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
          >
            <div className="font-display font-[900] text-[2rem] md:text-[2.5rem] leading-none tracking-[-0.03em]">
              {s.value}
            </div>
            <div className="mt-2 text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
              {s.label}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* =============================================================== */
/*  ACTIVE CLUBS                                                    */
/* =============================================================== */

const ActiveClubs: React.FC<{ clubs: BookClub[]; onJoin: (club: BookClub) => void }> = ({ clubs, onJoin }) => (
  <section id="clubs" className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
    <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
      <div>
        <Kicker label="The clubs" />
        <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[22ch]">
          Active clubs.{' '}
          One common
          shelf.
        </h2>
      </div>
    </div>

    {clubs.length === 0 ? (
      <div className="text-center py-20 text-[var(--muted)] text-[14px] font-mono uppercase tracking-wider">
        No active clubs at the moment.
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {clubs.map((c, i) => (
          <ClubCard key={c.slug} club={c} delay={i * 0.06} onJoin={onJoin} />
        ))}
      </div>
    )}
  </section>
);

const ClubCard: React.FC<{ club: BookClub; delay: number; onJoin: (club: BookClub) => void }> = ({ club, delay, onJoin }) => {
  const dotColor =
    club.activity_level === 'high'
      ? 'bg-[var(--gold)]'
      : club.activity_level === 'medium'
      ? 'bg-[var(--primary-soft)]'
      : 'bg-[var(--muted)]';
  const activityLabel =
    club.activity_level === 'high'
      ? 'Very active'
      : club.activity_level === 'medium'
      ? 'Active'
      : 'Quiet';

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -4 }}
      className="group rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden hover:border-[var(--ink)]/30 hover:shadow-[0_24px_50px_-24px_rgba(20,24,43,0.25)] transition-all cursor-pointer"
    >
      {/* Top: name + activity */}
      <div className="p-6 pb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <span className="inline-block text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--ink-soft)] mb-2.5">
            {club.tag}
          </span>
          <h3 className="font-display font-bold text-[1.6rem] leading-[1.05] tracking-tight text-[var(--ink)]">
            {club.name}
          </h3>
          <p className="mt-1.5 text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
            Advised by {club.advisor_name}
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-1.5">
          <span className="relative flex w-2 h-2">
            <span className={`absolute inline-flex w-full h-full rounded-full ${dotColor} opacity-60 animate-ping`} />
            <span className={`relative inline-flex w-2 h-2 rounded-full ${dotColor}`} />
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)]">
            {activityLabel}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="px-6 text-[13.5px] text-[var(--ink-soft)] leading-[1.5] font-serif italic">
        "{club.description}"
      </p>

      {/* Currently reading */}
      {club.current_book && (
        <div className="mt-5 mx-6 mb-5 rounded-xl bg-[var(--surface-2)] p-4 flex items-center gap-3 border border-[var(--border)]">
          <div
            className="relative w-10 h-14 rounded shrink-0 shadow-[inset_-2px_0_0_rgba(0,0,0,0.18)] overflow-hidden"
            style={{ backgroundColor: club.tint || 'var(--primary)' }}
          >
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.18),transparent_60%)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--muted)]">
              Currently reading
            </div>
            <div className="font-medium text-[14px] truncate text-[var(--ink)]">
              {club.current_book.title}
            </div>
            <div className="text-[12px] text-[var(--ink-soft)] truncate font-serif italic">
              {club.current_book.author}
            </div>
            <div className="mt-1.5 h-1 bg-[var(--border)] rounded-full overflow-hidden">
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: club.current_book.progress / 100 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: delay + 0.2, ease: 'easeOut' }}
                className="h-full bg-[var(--gold)] origin-left rounded-full"
              />
            </div>
            <div className="mt-1 text-[10px] text-[var(--muted)] font-mono uppercase tracking-wider">
              {club.current_book.progress}% through
            </div>
          </div>
        </div>
      )}

      {/* Footer: schedule + members */}
      <div className="px-6 py-4 border-t border-[var(--border)] bg-[var(--surface-2)]/40 flex items-center justify-between text-[12.5px]">
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-1.5 text-[var(--ink-soft)]">
            <CalendarClock className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{club.schedule_text}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[var(--muted)]">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{club.location}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-display font-bold text-[1.35rem] tracking-tight leading-none">
            {club.member_count}
          </div>
          <div className="text-[10px] text-[var(--muted)] font-mono uppercase tracking-wider mt-1 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> members
          </div>
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onJoin(club); }}
        className="w-full px-6 py-3 border-t border-[var(--border)] flex items-center justify-between hover:bg-[var(--gold)]/5 transition-colors"
      >
        <span className="text-[12px] font-mono uppercase tracking-wider text-[var(--ink-soft)] group-hover:text-[var(--ink)]">
          Join the club
        </span>
        <ArrowUpRight className="w-4 h-4 text-[var(--muted)] group-hover:text-[var(--gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </button>
    </motion.article>
  );
};

/* =============================================================== */
/*  LATEST DISCUSSIONS                                              */
/* =============================================================== */

const LatestDiscussions: React.FC<{ discussions: DiscussionThread[] }> = ({ discussions }) => (
  <section className="border-y border-[var(--border)] bg-[var(--surface-2)]">
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-24 pb-20">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <Kicker label="Latest threads" />
          <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[24ch]">
            What readers are saying.
          </h2>
        </div>
      </div>

      {discussions.length === 0 ? (
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] py-16 text-center text-[var(--muted)] text-[14px] font-mono uppercase tracking-wider">
          No discussions yet. Be the first to start one.
        </div>
      ) : (
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
          {discussions.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="border-b border-[var(--border)] last:border-0"
            >
            <Link
              to={`/book-clubs/threads/${d.id}`}
              className="group flex items-start gap-4 md:gap-6 p-5 md:p-6 hover:bg-[var(--surface-2)] transition-colors"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-[var(--gold)]/10 border border-[var(--gold)]/30 flex items-center justify-center">
                <Hash className="w-4 h-4 text-[var(--gold)]" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[11px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[var(--ink)]/5 text-[var(--ink-soft)]">
                    {d.club_slug}
                  </span>
                  {d.is_hot && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[var(--gold)]/15 text-[var(--ink)] inline-flex items-center gap-1">
                      <Flame className="w-3 h-3" /> Hot
                    </span>
                  )}
                </div>
                <div className="font-medium text-[15px] leading-[1.4] text-[var(--ink)] group-hover:text-[var(--primary)] transition-colors">
                  {d.title}
                </div>
                <div className="mt-1.5 text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
                  Started by {d.author_name} · {timeAgo(d.last_reply_at ?? d.created_at)} ago
                </div>
              </div>

              <div className="shrink-0 hidden sm:flex flex-col items-end text-right">
                <div className="font-display font-bold text-[1.35rem] leading-none">{d.reply_count}</div>
                <div className="text-[10px] text-[var(--muted)] font-mono uppercase tracking-wider mt-1 inline-flex items-center gap-1">
                  <MessagesSquare className="w-3 h-3" /> replies
                </div>
              </div>

              <ArrowUpRight className="hidden md:block shrink-0 w-4 h-4 text-[var(--muted)] mt-3 group-hover:text-[var(--ink)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  </section>
);

/* =============================================================== */
/*  FEATURED VOICE                                                  */
/* =============================================================== */

const FeaturedVoiceSection: React.FC<{ voice: FeaturedVoice }> = ({ voice }) => (
  <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-10">
    <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
      <div className="col-span-12 lg:col-span-4">
        <Kicker label="Voice of the week" />
        <div className="mt-8 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/40 flex items-center justify-center">
            <Feather className="w-6 h-6 text-[var(--gold-soft)]" />
          </div>
          <div>
            <div className="font-display font-bold text-[1.25rem] leading-none text-[var(--ink)]">
              {voice.by}
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--muted)]">
              {voice.year_label} · {voice.club}
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-8 relative">
        <Quote className="absolute -top-6 -left-4 w-20 h-20 text-[var(--gold)]/25" strokeWidth={1} />
        <blockquote className="font-display font-[350] text-[1.65rem] sm:text-[2.1rem] md:text-[2.5rem] leading-[1.18] tracking-[-0.01em] text-[var(--ink)]">
          "{voice.quote}"
        </blockquote>
        <div className="mt-8 h-px bg-[var(--border)]" />
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--muted)] inline-flex items-center gap-2">
          <MessagesSquare className="w-3.5 h-3.5" /> on the thread "{voice.thread}"
        </p>
      </div>
    </div>
  </section>
);

/* =============================================================== */
/*  THIS WEEK'S CALENDAR                                            */
/* =============================================================== */

const ThisWeekCalendar: React.FC<{ meetings: ClubMeeting[] }> = ({ meetings }) => (
  <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-10">
    <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
      <div>
        <Kicker label="This week" />
        <h2 className="mt-4 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[22ch]">
          When and{' '}
          where we meet.
        </h2>
      </div>
    </div>

    {meetings.length === 0 ? (
      <div className="border-y border-[var(--border)] py-16 text-center text-[var(--muted)] text-[14px] font-mono uppercase tracking-wider">
        No upcoming meetings in the next two weeks.
      </div>
    ) : (
      <div className="border-y border-[var(--border)]">
        {meetings.map((m, i) => {
          const { day, month, weekday } = parseMeetingDate(m.date);
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group grid grid-cols-12 gap-4 md:gap-6 py-6 md:py-7 items-center border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface-2)] px-3 -mx-3 rounded-xl transition-colors"
            >
              <div className="col-span-12 md:col-span-2 flex md:block items-center gap-3">
                <div className="font-display font-[900] text-[2.75rem] md:text-[3.5rem] leading-none tracking-[-0.03em] text-[var(--ink)]">
                  {day}
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--gold-text)] leading-none">
                    {month}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] leading-none mt-1.5">
                    {weekday}
                  </div>
                </div>
              </div>

              <div className="col-span-12 md:col-span-7">
                <span className="inline-block text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded bg-[var(--ink)]/5 text-[var(--ink-soft)] mb-1.5">
                  {m.club_name}
                </span>
                <h3 className="font-display font-bold text-[1.25rem] md:text-[1.4rem] leading-tight tracking-tight">
                  {m.topic}
                </h3>
              </div>

              <div className="col-span-12 md:col-span-3 md:text-right space-y-1.5">
                <div className="flex items-center gap-1.5 md:justify-end text-[12.5px] text-[var(--ink-soft)]">
                  <CalendarClock className="w-3.5 h-3.5" /> {formatMeetingTime(m.start_time, m.end_time)}
                </div>
                <div className="flex items-center gap-1.5 md:justify-end text-[12.5px] text-[var(--muted)]">
                  <MapPin className="w-3.5 h-3.5" /> {m.location}
                </div>
                <div className="flex items-center gap-1.5 md:justify-end pt-2 text-[11px] font-mono uppercase tracking-wider text-[var(--muted)] group-hover:text-[var(--gold)] transition-colors">
                  RSVP
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    )}
  </section>
);

/* =============================================================== */
/*  START A CLUB                                                    */
/* =============================================================== */

const StartAClub: React.FC<{ onPropose: () => void }> = ({ onPropose }) => (
  <section id="start" className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-24">
    <div className="relative rounded-3xl bg-[var(--ink)] text-[var(--paper)] p-10 md:p-16 overflow-hidden">
      <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-[var(--paper)]/10" />
      <div className="absolute -right-12 -bottom-12 w-72 h-72 rounded-full border border-[var(--paper)]/10" />
      <div className="absolute right-6 bottom-6 w-40 h-40 rounded-full bg-[var(--gold)]/20 blur-3xl" />

      <div className="relative grid grid-cols-12 gap-6 md:gap-10 items-end">
        <div className="col-span-12 lg:col-span-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--paper)]/60 mb-5 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Get started
          </div>
          <h2 className="font-display font-[900] text-[2rem] sm:text-[2.5rem] md:text-[3.75rem] leading-[0.98] tracking-[-0.03em]">
            Start your own club.
          </h2>
          <p className="mt-6 text-[var(--paper)]/70 max-w-[52ch] text-[15px] leading-[1.55]">
            Pick a faculty advisor, propose your first read, set a
            meeting time. We'll handle the room, the notifications, and the
            attendance sheet. You bring the book.
          </p>
        </div>

        <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
          <button
            onClick={onPropose}
            className="inline-flex items-center gap-2 bg-[var(--gold)] text-[#1E2D5C] px-5 py-3 rounded-xl font-semibold text-[14px] hover:brightness-110 transition-all shadow-[0_4px_14px_-4px_rgba(201,168,103,0.6)]"
          >
            Propose a club <Plus className="w-4 h-4" />
          </button>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 border border-[var(--paper)]/25 hover:border-[var(--paper)]/60 text-[var(--paper)] px-5 py-3 rounded-xl font-medium text-[14px] transition-colors"
          >
            How it works
          </Link>
        </div>
      </div>

      <div className="relative mt-12 pt-8 border-t border-[var(--paper)]/15 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { num: '01', t: 'Pick an advisor', d: 'Any faculty member can sponsor a club.' },
          { num: '02', t: 'Propose the first read', d: 'The library sources copies for everyone.' },
          { num: '03', t: 'Schedule a slot', d: 'Agree a regular time and room with the librarian.' },
        ].map((s) => (
          <div key={s.num} className="flex gap-4">
            <div className="font-display font-[900] text-[1.75rem] leading-none text-[var(--gold-text)] tracking-tight">
              {s.num}
            </div>
            <div>
              <div className="font-medium text-[15px] text-[var(--paper)]">{s.t}</div>
              <div className="mt-1 text-[13px] text-[var(--paper)]/65 leading-[1.5]">{s.d}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px] font-mono uppercase tracking-wider text-[var(--paper)]/55">
        <span className="inline-flex items-center gap-1.5">
          <Star className="w-3 h-3 text-[var(--gold)] fill-[var(--gold)]" strokeWidth={0} />
          Proposals reviewed within 48 hours
        </span>
        <span>Submit via the form · get a decision by email</span>
      </div>
    </div>
  </section>
);

/* =============================================================== */
/*  PROPOSE A CLUB MODAL                                           */
/* =============================================================== */

const EMPTY_FORM: ProposalData = {
  name: '',
  tag: '',
  advisor_name: '',
  first_read: '',
  description: '',
};

const ProposeClubModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [form, setForm] = useState<ProposalData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => firstRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  function set(field: keyof ProposalData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const missing = (['name', 'tag', 'advisor_name', 'first_read', 'description'] as const).filter(
      (k) => !form[k].trim()
    );
    if (missing.length) {
      toast.error('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await bookclubProposalsApi.submitProposal(form);
      toast.success("Proposal submitted! We'll review it within 48 hours.");
      setForm(EMPTY_FORM);
      onClose();
    } catch {
      toast.error('Could not submit your proposal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    /* Rendered inside .smchs-home so CSS vars resolve; position:fixed still covers viewport */
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div className="relative z-10 w-full sm:max-w-lg bg-[var(--surface)] border border-[var(--border)] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92dvh] overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[var(--border)] shrink-0 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-[1.25rem] tracking-tight text-[var(--ink)]">
              Propose a new club
            </h2>
            <p className="text-[13px] text-[var(--ink-soft)] mt-0.5">
              Your proposal goes to the librarian for review. You'll be notified by email.
            </p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable fields */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            <Field label="Club name *">
              <input
                ref={firstRef}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. The Dead Poets"
                className={inputCls}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Genre / tag *">
                <input
                  value={form.tag}
                  onChange={(e) => set('tag', e.target.value)}
                  placeholder="e.g. Poetry"
                  className={inputCls}
                />
              </Field>
              <Field label="Faculty advisor *">
                <input
                  value={form.advisor_name}
                  onChange={(e) => set('advisor_name', e.target.value)}
                  placeholder="e.g. Mr. Daniel"
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="First book to read *">
              <input
                value={form.first_read}
                onChange={(e) => set('first_read', e.target.value)}
                placeholder="e.g. Devotions by Mary Oliver"
                className={inputCls}
              />
            </Field>

            <Field label="Why this club? *">
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="What will you discuss? Who is it for?"
                rows={3}
                className={`${inputCls} resize-none`}
              />
            </Field>
          </div>

          {/* Footer actions */}
          <div className="px-6 py-4 border-t border-[var(--border)] bg-[var(--surface-2)]/60 shrink-0 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--ink-soft)] text-[13px] hover:bg-[var(--surface-2)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:bg-[var(--primary)] transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Submit proposal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] text-[var(--ink)] text-[14px] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/50';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--muted)] mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

export default BookClubPage;
